'use client';
import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import StrapiBlocksRenderer from '@/components/ui/StrapiBlocksRenderer';
import useStrapiHome from '@/hooks/useStrapiHome';

interface InfoCard {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  visible?: boolean;
  url?: string | null;
  imagen?: {
    id: number;
    name: string;
    url: string;
    formats?: {
      thumbnail?: { url: string };
      large?: { url: string };
      medium?: { url: string };
      small?: { url: string };
    };
  };
  backgroundColor?: string;
}

interface InfoCardsWithRecipeMobileProps {
  className?: string;
  cards?: InfoCard[];
}

const InfoCardsWithRecipeMobile: React.FC<InfoCardsWithRecipeMobileProps> = ({ 
  className = '',
  cards = []
}) => {
  const router = useRouter();
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout>(undefined);
  const { data: strapiData, loading, error } = useStrapiHome();

  // Colores de fondo por defecto para las cards
  const defaultBackgrounds = ['bg-professional-custom', 'bg-global-4', 'bg-global-3'];

  // Procesar cards desde los datos de Strapi
  const strapiCards: InfoCard[] = strapiData?.infoCards ? 
    strapiData.infoCards
      .filter(card => card.visible !== false) // Filtrar cards visibles
      .map((card, index) => ({
        ...card,
        backgroundColor: defaultBackgrounds[index] || 'bg-global-3'
      })) : [];
  // Cards por defecto si no hay datos de Strapi
  const defaultCards: InfoCard[] = [
    {
      id: 1,
      title: '¿Eres profesional?',
      subtitle: 'Ut enim ad minim veniam',
      description: 'Descubre nuestros productos especiales para profesionales de la gastronomía.',
      backgroundColor: 'bg-professional-custom'
    },
    {
      id: 2,
      title: 'Recetas',
      subtitle: 'Deliciosas creaciones',
      description: 'Encuentra las mejores recetas con ingredientes auténticos italianos.',
      backgroundColor: 'bg-global-4'
    },
    {
      id: 3,
      title: 'Marcas',
      subtitle: 'Calidad garantizada',
      description: 'Conoce nuestras marcas premium directamente importadas de Italia.',
      backgroundColor: 'bg-global-3'
    }
  ];

  // Si hay datos de Strapi pero ninguna card es visible, no renderizar la sección
  if (strapiData?.infoCards && strapiData.infoCards.length > 0 && strapiCards.length === 0) {
    return null;
  }

  // Prioridad: props cards > Strapi cards > default cards
  const cardsToUse = cards.length > 0 ? cards : (strapiCards.length > 0 ? strapiCards : defaultCards);

  // Función para manejar el cambio de tarjeta
  const handleCardChange = (index: number) => {
    if (index !== selectedCard && index >= 0 && index < cardsToUse.length) {
      setSelectedCard(index);

      // Actualizar el scroll del carrusel
      if (carouselRef.current) {
        const slideWidth = carouselRef.current.offsetWidth;
        carouselRef.current.scrollTo({
          left: slideWidth * index,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleCardClick = (card: InfoCard, e: React.MouseEvent) => {
    // Solo redirigir si el usuario no está arrastrando
    if (!isDragging && card.url) {
      e.preventDefault();
      router.push(card.url);
    }
  };

  // Función para ir a la siguiente tarjeta
  const goToNextCard = () => {
    const nextIndex = (selectedCard + 1) % cardsToUse.length;
    handleCardChange(nextIndex);
  };

  // Configurar autoplay
  useEffect(() => {
    const startAutoPlay = () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
      autoPlayRef.current = setInterval(goToNextCard, 5000);
    };

    if (!isDragging) {
      startAutoPlay();
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [selectedCard, isDragging]);

  // Manejadores de eventos para el drag
  const handleDragStart = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }

    const pageX = 'touches' in e ? e.touches[0].pageX : e.pageX;
    setStartX(pageX);
    if (carouselRef.current) {
      setScrollLeft(carouselRef.current.scrollLeft);
    }
  };

  const handleDragMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    e.preventDefault();
    const pageX = 'touches' in e ? e.touches[0].pageX : e.pageX;
    const walk = (startX - pageX) * 2;
    
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = scrollLeft + walk;
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (!carouselRef.current) return;

    const slideWidth = carouselRef.current.offsetWidth;
    const scrollPosition = carouselRef.current.scrollLeft;
    const newIndex = Math.round(scrollPosition / slideWidth);
    
    setSelectedCard(newIndex);
  };

  // Manejar el scroll del carrusel
  const handleScroll = () => {
    if (!carouselRef.current || isDragging) return;

    const slideWidth = carouselRef.current.offsetWidth;
    const scrollPosition = carouselRef.current.scrollLeft;
    const newIndex = Math.round(scrollPosition / slideWidth);

    if (newIndex !== selectedCard) {
      setSelectedCard(newIndex);
    }
  };

  return (
    <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:192:4" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="192" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`py-0.5 ${className}`}>
      {/* Carrusel único: cada slide = imagen + card (mismo efecto para ambos) */}
      <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:194:6" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="194" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20overflow-x-auto%20scrollbar-hide%22%7D"
        ref={carouselRef}
        className="relative overflow-x-auto scrollbar-hide"
        onScroll={handleScroll}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'x mandatory'
        }}
      >
        <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:211:8" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="211" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%22%7D" className="flex">
          {cardsToUse.map((card, index) => (
            <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:213:12" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="213" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-none%20w-full%20snap-center%22%7D"
              key={card.id}
              className="flex-none w-full snap-center"
            >
              {/* Imagen de este slide */}
              <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:218:14" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="218" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20rounded-t-%5B12px%5D%20overflow-hidden%20aspect-%5B1200%2F526%5D%20w-full%22%7D" className="relative rounded-t-[12px] overflow-hidden aspect-[1200/526] w-full">
                <Image data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:219:16" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="219" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-cover%22%7D"
                  src={card.imagen?.url || '/images/img_receta_1.png'}
                  alt={card.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 526px"
                />
              </div>
              {/* Card de este slide */}
              <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:228:14" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="228" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`${card.backgroundColor || 'bg-global-3'} rounded-t-none rounded-b-[12px] p-4 transition-all duration-300 ${
                  selectedCard === index ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
                }`}
                style={{ height: '160.95px', cursor: card.url ? 'pointer' : 'grab' }}
                onClick={(e) => handleCardClick(card, e)}
              >
                <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:235:16" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="235" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-3%20inline-block%20mb-2%22%7D" className="bg-global-9 rounded px-3 inline-block mb-2">
                  <span data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:236:18" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="236" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-sm%20leading-4%20text-global-1%22%7D" className="font-tt-norms-medium text-sm leading-4 text-global-1">
                    {card.title}
                  </span>
                </div>
                <h3 data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:240:16" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="240" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-sm%20leading-4%20uppercase%20text-global-1%20mb-1%22%7D" className="font-tt-norms-extra-black text-sm leading-4 uppercase text-global-1 mb-1">
                  {card.subtitle}
                </h3>
                <p data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:243:16" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="243" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-medium%20text-base%20leading-4%20text-global-1%22%7D" className="font-tt-norms-medium text-base leading-4 text-global-1">
                  {card.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Indicadores de página */}
      <div data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:253:6" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="253" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20mt-4%20gap-2%22%7D" className="flex justify-center mt-4 gap-2">
        {cardsToUse.map((_, index) => (
          <button data-component-id="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx:255:10" data-component-path="src/components/sections/mobile/InfoCardsWithRecipeMobile.tsx" data-component-line="255" data-component-file="InfoCardsWithRecipeMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            key={index}
            onClick={() => handleCardChange(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              selectedCard === index 
                ? 'w-6 bg-[#3d1c1a]' 
                : 'w-2 bg-global-2 hover:bg-[#3d1c1a]/50'
            }`}
            aria-label={`Ir a la tarjeta ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default InfoCardsWithRecipeMobile;